/** @format */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FunidataFeatureToggles } from 'common-typescript/types';
import { map, Observable, of } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';

const ALL_FEATURES_DISABLED: FunidataFeatureToggles = {
    studyVoucherPaymentEnabled: false,
    oauth2AuthorizationEnabled: false,
};

function isNonEmptyObject(toggles: unknown): boolean {
    return !!toggles && typeof toggles === 'object' && Object.keys(toggles).length > 0;
}

/**
 * This service provides access to the feature toggles configured for the environment. It's the frontend equivalent
 * of the `FunidataFeatureToggles` injectable that is used in backend services.
 *
 * If no feature toggle configuration exists for the environment, or fetching the configuration fails, the service
 * will fall back to disabling all feature toggles.
 */
@Injectable({ providedIn: 'root' })
export class FeatureToggleService {
    private readonly http = inject(HttpClient);

    private readonly featureToggles$: Observable<FunidataFeatureToggles> = this.http
        .get<FunidataFeatureToggles>('featureToggles.json')
        .pipe(
            map(toggles => (isNonEmptyObject(toggles) ? toggles : ALL_FEATURES_DISABLED)),
            catchError(() => {
                console.warn('Could not load feature toggle configuration, all feature toggles considered disabled');
                // Feature toggle configuration is optional, so just return default values if loading fails
                return of(ALL_FEATURES_DISABLED);
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
        );

    /**
     * See {@link FunidataFeatureToggles.studyVoucherPaymentEnabled}.
     */
    isStudyVoucherPaymentEnabled(): Observable<boolean> {
        return this.createBooleanToggleStream('studyVoucherPaymentEnabled');
    }

    private createBooleanToggleStream(toggleFieldName: keyof FunidataFeatureToggles): Observable<boolean> {
        return this.featureToggles$.pipe(map(toggles => toggles[toggleFieldName] === true));
    }
}
